import { IconDots, IconExternalLink } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, linkOptions, Outlet } from "@tanstack/react-router";
import z from "zod";
import { SiteHeader } from "@/components/site-header";
import { StatusBadge } from "@/components/status-badge";
import { TabBar, type TabBarTabs } from "@/components/tab-bar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs } from "@/components/ui/tabs";
import { getChannelStatus } from "@/enums/channel-status";
import { getDashboardUrl, getShowUrl } from "@/lib/url";
import { orpc } from "@/orpc/client";

export const Route = createFileRoute("/_app/channels/$channelId")({
	component: Page,
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.channels.show.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data } = useSuspenseQuery(
		orpc.channels.show.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const tabs: TabBarTabs = linkOptions([
		{
			key: "details",
			to: "/channels/$channelId",
			params: { channelId: data.ID },
			activeOptions: { exact: true },
			label: "Details",
		},
		{
			key: "monetization",
			to: "/channels/$channelId/monetization",
			params: { channelId: data.ID },
			label: "Monetization",
		},
		{
			key: "audio",
			to: "/channels/$channelId/audio",
			params: { channelId: data.ID },
			label: "Audio",
		},
		{
			key: "jingles",
			to: "/channels/$channelId/jingles",
			params: { channelId: data.ID },
			label: "Jingles",
		},
		{
			key: "team",
			to: "/channels/$channelId/team",
			params: { channelId: data.ID },
			label: "Team",
			count: data.usersCount,
		},
		{
			key: "directories",
			to: "/channels/$channelId/directories",
			params: { channelId: data.ID },
			label: "Directories",
		},
		{
			key: "episodes",
			to: "/channels/$channelId/episodes",
			params: { channelId: data.ID },
			label: "Episodes",
			count: data.episodesCount,
		},
		{
			key: "files",
			to: "/channels/$channelId/files",
			params: { channelId: data.ID },
			label: "Files",
		},
		{
			key: "share",
			to: "/channels/$channelId/share",
			params: { channelId: data.ID },
			label: "Share",
		},
		{
			key: "settings",
			to: "/channels/$channelId/settings",
			params: { channelId: data.ID },
			label: "Settings",
		},
	]);

	return (
		<>
			<SiteHeader
				breadcrumbs={[
					{
						key: "channels",
						label: "Channels",
						linkProps: { to: "/channels" },
					},
					{
						key: "channel",
						label: data.Title ?? "Channel Details",
					},
				]}
			/>
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
						<div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
							<div className="flex w-full flex-col justify-between gap-2 md:flex-row md:gap-4 lg:items-end">
								<div className="flex gap-3">
									<Avatar className="size-16" rounded="sm">
										<AvatarImage
											src={
												data.Icon?.ThumbnailURL
													? `${import.meta.env.VITE_CDN_URL}${data.Icon.ThumbnailURL}`
													: undefined
											}
										/>
										<AvatarFallback>{data.Title?.slice(0, 1).toUpperCase()}</AvatarFallback>
									</Avatar>
									<div>
										<h1 className="text-lg leading-tight font-semibold">
											{data.Title} <StatusBadge status={getChannelStatus(data.StatusID)} />
										</h1>
										<p className="text-muted-foreground text-sm">{data.HomepageUrl}</p>
										<p className="text-muted-foreground text-sm">Channel ID: #{data.ID}</p>
									</div>
								</div>
								<div className="flex items-center justify-end gap-2">
									<ButtonGroup>
										<Button
											variant="outline"
											size="sm"
											nativeButton={false}
											render={
												<a
													href={getShowUrl(data.UrlTitle)}
													target="_blank"
													rel="noopener noreferrer"
												>
													<IconExternalLink />
													Open Show
												</a>
											}
										/>
										<DropdownMenu>
											<DropdownMenuTrigger
												render={
													<Button variant="outline" size="icon-sm" aria-label="More Options">
														<IconDots />
													</Button>
												}
											/>
											<DropdownMenuContent align="end" className="min-w-40">
												<DropdownMenuGroup>
													<DropdownMenuItem
														className="whitespace-nowrap"
														render={
															<a
																href={getDashboardUrl(`/podcasts/${data.UID}/settings/details`)}
																target="_blank"
																rel="noopener noreferrer"
															/>
														}
													>
														<IconExternalLink />
														Open in Dashboard
													</DropdownMenuItem>
													<DropdownMenuItem
														render={
															<a
																href={getShowUrl(data.UrlTitle)}
																target="_blank"
																rel="noopener noreferrer"
															/>
														}
													>
														<IconExternalLink />
														Open Show Page
													</DropdownMenuItem>
												</DropdownMenuGroup>
											</DropdownMenuContent>
										</DropdownMenu>
									</ButtonGroup>
								</div>
							</div>
						</div>
						<Tabs>
							<div className="px-4 lg:px-6">
								<TabBar tabs={tabs} />
							</div>
							<Outlet />
						</Tabs>
					</div>
				</div>
			</div>
		</>
	);
}
